import { useEffect, useState, useRef, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';
import { getConfig } from '../../../../framework/config/app.config';

/**
 * React hook for OpenAI Realtime API voice conversations
 * 
 * Provides low-latency (<300ms), bi-directional voice chat with:
 * - Real-time audio streaming
 * - Automatic voice activity detection
 * - Natural interruptions
 * - Live transcripts
 * - Function calling during voice conversations
 */

export interface RealtimeVoiceConfig {
  assistantId?: string;
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  instructions?: string;
  enableFunctions?: boolean;
  serverUrl?: string;
}

export interface RealtimeVoiceState {
  isConnected: boolean;
  isRecording: boolean;
  isSpeaking: boolean;
  transcript: string;
  status: string;
  error: string | null;
}

export interface RealtimeVoiceActions {
  startSession: (config: RealtimeVoiceConfig) => void;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  interrupt: () => void;
  endSession: () => void;
}

export function useRealtimeVoice(): RealtimeVoiceState & RealtimeVoiceActions {
  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [status, setStatus] = useState('Disconnected');
  const [error, setError] = useState<string | null>(null);

  // Refs for audio handling
  const socketRef = useRef<Socket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const playbackAudioContextRef = useRef<AudioContext | null>(null);

  // Initialize socket connection
  useEffect(() => {
    // Use the same API URL configuration as the rest of the app
    const serverUrl = getConfig('VITE_BASE_URL') || 'http://localhost:3000';
    console.log('🔌 Connecting to Realtime API at:', serverUrl + '/realtime');
    
    const newSocket = io(serverUrl + '/realtime', {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('✅ Connected to Realtime API gateway');
      setIsConnected(true);
      setStatus('Connected');
      setError(null);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ Disconnected from Realtime API gateway');
      setIsConnected(false);
      setStatus('Disconnected');
      cleanup();
    });

    newSocket.on('connect_error', (err) => {
      console.error('Connection error:', err);
      setError(`Connection failed: ${err.message}`);
      setStatus('Connection Error');
    });

    // Session events
    newSocket.on('session-started', (data) => {
      console.log('🎙️ Realtime session started:', data);
      setStatus('Ready');
    });

    newSocket.on('openai-connected', () => {
      console.log('🟢 OpenAI Realtime API connected');
      setStatus('OpenAI Connected');
    });

    newSocket.on('openai-disconnected', () => {
      console.log('🔴 OpenAI Realtime API disconnected');
      setStatus('OpenAI Disconnected');
    });

    // Speech detection
    newSocket.on('speech-started', () => {
      console.log('🎤 User started speaking');
      setIsSpeaking(false); // User speaking, AI not speaking
    });

    newSocket.on('speech-stopped', () => {
      console.log('🎤 User stopped speaking');
    });

    // Transcripts
    newSocket.on('transcript', (data) => {
      console.log('📝 Transcript:', data.text);
      setTranscript(data.text);
    });

    // AI response events
    newSocket.on('response-started', () => {
      console.log('🤖 AI started responding');
      setIsSpeaking(true);
      setStatus('AI Speaking');
    });

    newSocket.on('response-completed', () => {
      console.log('✅ AI finished responding');
      setIsSpeaking(false);
      setStatus('Ready');
    });

    // Audio streaming
    newSocket.on('audio-delta', (data) => {
      // Receive audio chunk from AI
      playAudioChunk(data.audio);
    });

    newSocket.on('audio-done', () => {
      console.log('🔊 Audio playback complete');
    });

    // Text deltas (for debugging or display)
    newSocket.on('text-delta', (data) => {
      console.log('💬 Text delta:', data.text);
    });

    // Function execution
    newSocket.on('function-executed', (data) => {
      console.log('⚙️ Function executed:', data.functionName);
    });

    newSocket.on('function-error', (data) => {
      console.error('❌ Function error:', data.error);
    });

    // Errors
    newSocket.on('error', (err) => {
      console.error('❌ Realtime API error:', err);
      setError(err.message || 'Unknown error');
      setStatus('Error');
    });

    socketRef.current = newSocket;

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up Realtime voice hook');
      cleanup();
      newSocket.close();
    };
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (playbackAudioContextRef.current) {
      playbackAudioContextRef.current.close();
      playbackAudioContextRef.current = null;
    }

    setIsRecording(false);
    setIsSpeaking(false);
  }, []);

  // Audio chunk player
  const playAudioChunk = useCallback(async (base64Audio: string) => {
    try {
      // Decode base64 to PCM16
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM16 to Float32
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }

      // Create audio context if needed
      if (!playbackAudioContextRef.current) {
        playbackAudioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      const audioContext = playbackAudioContextRef.current;

      // Create audio buffer
      const audioBuffer = audioContext.createBuffer(1, float32.length, 24000);
      audioBuffer.getChannelData(0).set(float32);

      // Queue audio
      audioQueueRef.current.push(audioBuffer);

      // Start playback if not already playing
      if (!isPlayingRef.current) {
        playNextInQueue();
      }
    } catch (error) {
      console.error('Error playing audio chunk:', error);
    }
  }, []);

  // Play queued audio
  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    isPlayingRef.current = true;
    const buffer = audioQueueRef.current.shift()!;
    const audioContext = playbackAudioContextRef.current!;

    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);

    source.onended = () => {
      playNextInQueue();
    };

    source.start();
  }, []);

  // Start session
  const startSession = useCallback((config: RealtimeVoiceConfig) => {
    if (!socketRef.current?.connected) {
      console.error('Cannot start session: socket not connected');
      return;
    }

    console.log('🎙️ Starting Realtime session with config:', config);
    
    socketRef.current.emit('start-session', {
      assistantId: config.assistantId,
      voice: config.voice || 'alloy',
      instructions: config.instructions,
      enableFunctions: config.enableFunctions !== false, // Default true
    });
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      console.log('🎤 Starting recording...');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      mediaStreamRef.current = stream;

      // Create audio context
      const audioContext = new AudioContext({ sampleRate: 24000 });
      audioContextRef.current = audioContext;

      // Load audio worklet
      try {
        await audioContext.audioWorklet.addModule('/audio-processor.js');
      } catch (error) {
        console.error('Failed to load audio worklet:', error);
        throw new Error('Audio processor not found. Make sure audio-processor.js is in your public folder.');
      }

      // Create source from microphone
      const source = audioContext.createMediaStreamSource(stream);

      // Create worklet node
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');
      workletNodeRef.current = workletNode;

      // Handle audio data from worklet
      workletNode.port.onmessage = (event) => {
        const audioData = event.data; // Float32Array

        // Convert to PCM16 and send to server
        const pcm16 = floatTo16BitPCM(audioData);
        const base64Audio = arrayBufferToBase64(pcm16.buffer as ArrayBuffer);

        if (socketRef.current?.connected) {
          socketRef.current.emit('audio-chunk', { audio: base64Audio });
        }
      };

      // Connect nodes
      source.connect(workletNode);
      // Don't connect to destination (no local echo)

      setIsRecording(true);
      setStatus('Recording');
      console.log('✅ Recording started');

    } catch (error: any) {
      console.error('Error starting recording:', error);
      setError(`Microphone access failed: ${error.message}`);
      setStatus('Microphone Error');
    }
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    console.log('⏹️ Stopping recording');
    
    cleanup();
    
    // Commit audio to signal end of speech
    if (socketRef.current?.connected) {
      socketRef.current.emit('commit-audio');
    }

    setIsRecording(false);
    setStatus('Processing');
  }, [cleanup]);

  // Interrupt AI
  const interrupt = useCallback(() => {
    console.log('⏸️ Interrupting AI');
    
    if (socketRef.current?.connected) {
      socketRef.current.emit('interrupt');
    }

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    
    setIsSpeaking(false);
    setStatus('Interrupted');
  }, []);

  // End session
  const endSession = useCallback(() => {
    console.log('🛑 Ending Realtime session');
    
    cleanup();
    
    if (socketRef.current?.connected) {
      socketRef.current.emit('end-session');
    }

    setTranscript('');
    setStatus('Session Ended');
  }, [cleanup]);

  return {
    // State
    isConnected,
    isRecording,
    isSpeaking,
    transcript,
    status,
    error,

    // Actions
    startSession,
    startRecording,
    stopRecording,
    interrupt,
    endSession,
  };
}

// Helper: Convert Float32Array to Int16Array (PCM16)
function floatTo16BitPCM(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16Array;
}

// Helper: Convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer: ArrayBufferLike): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
